import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  IBaseConfigurationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';

export enum TUiAlertTypes {
  WARNING = 'warning',
  SUCCESS = 'success',
  INFO = 'info',
  DANGER = 'danger',
}

interface IMessageLink {
  url: string;
  name: string;
  selfTarget?: boolean;
}

interface ISectionMessageFieldContentConfig {
  title?: string;
  message?: string;
  extraMessage?: string;
  extraMessageAsHtml?: boolean;
  messageType?: TUiAlertTypes;
  links?: IMessageLink[];
}

interface ISectionMessageConfigurationFormModel
  extends IBaseConfigurationFormModel {
  sectionMessageFieldContentConfig?: ISectionMessageFieldContentConfig;
}

export const sectionMessageWithLinkGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: {
        ...fieldsBaseConfigurationFormModel(),
        sectionMessageFieldContentConfig: {
          title: '',
          message: '',
          extraMessage: '',
          extraMessageAsHtml: false,
          messageType: TUiAlertTypes.WARNING,
          links: [],
        },
      },
      formlyForm: [...getSectionMessageFieldSettingsConfig()],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const sectionMessageFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): ISectionMessageConfigurationFormModel => {
  const model: ISectionMessageConfigurationFormModel = {
    sectionMessageFieldContentConfig: {
      title: field.props?.['title'],
      message: field.props?.['message'],
      extraMessage: field.props?.['extraMessage'],
      extraMessageAsHtml: field.props?.['extraMessageAsHtml'],
      messageType: field.props?.['messageType'],
      links: field.props?.['links'] || [],
    },
  };

  return model;
};

export const sectionMessageFieldUpdateFromSettingsFormModel = (
  model: ISectionMessageConfigurationFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (field.props) {
    field.props['title'] = model.sectionMessageFieldContentConfig?.title;
    field.props['message'] = model.sectionMessageFieldContentConfig?.message;
    field.props['extraMessage'] =
      model.sectionMessageFieldContentConfig?.extraMessage;
    field.props['extraMessageAsHtml'] =
      model.sectionMessageFieldContentConfig?.extraMessageAsHtml;
    field.props['messageType'] =
      model.sectionMessageFieldContentConfig?.messageType;
    field.props['links'] = model.sectionMessageFieldContentConfig?.links;
  } else {
    field.props = {
      title: model.sectionMessageFieldContentConfig?.title,
      message: model.sectionMessageFieldContentConfig?.message,
      extraMessage: model.sectionMessageFieldContentConfig?.extraMessage,
      extraMessageAsHtml:
        model.sectionMessageFieldContentConfig?.extraMessageAsHtml,
      messageType: model.sectionMessageFieldContentConfig?.messageType,
      links: model.sectionMessageFieldContentConfig?.links,
    };
  }

  return field;
};

export const sectionMessageFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const sectionMessageFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

export const getSectionMessageFieldSettingsConfig = (): FormlyFieldConfig[] => {
  return [
    {
      key: 'sectionMessageFieldContentConfig',
      id: 'sectionMessageFieldContentConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        dragDisabled: false,
      },
      fieldGroup: [
        {
          key: 'title',
          type: 'input',
          props: {
            label: 'Title',
            placeholder: 'Enter title',
          },
        },
        {
          key: 'message',
          type: 'textarea',
          props: {
            label: 'Message',
            placeholder: 'Enter message',
            rows: 3,
          },
        },
        {
          key: 'extraMessage',
          type: 'htmleditor',
          props: {
            label: 'Extra Message',
            placeholder: 'Enter extra message (HTML supported)',
            rows: 3,
          },
        },
        {
          key: 'extraMessageAsHtml',
          type: 'checkbox',
          props: {
            label: 'Render Extra Message as HTML',
          },
        },
        {
          key: 'messageType',
          type: 'select',
          props: {
            label: 'Message Type',
            required: true,
            options: [
              { label: 'Warning', value: TUiAlertTypes.WARNING },
              { label: 'Success', value: TUiAlertTypes.SUCCESS },
              { label: 'Info', value: TUiAlertTypes.INFO },
              { label: 'Danger', value: TUiAlertTypes.DANGER },
            ],
          },
        },
        {
          key: 'links',
          type: EIremboFormlyFieldTypes.customrepeater,
          props: {
            label: 'Links',
            addText: 'Add Link',
          },
          fieldArray: {
            type: EIremboFormlyFieldTypes['formly-group'],
            fieldGroup: [
              {
                key: 'url',
                type: 'input',
                props: {
                  label: 'URL',
                  required: true,
                  placeholder: 'Enter URL',
                },
              },
              {
                key: 'name',
                type: 'input',
                props: {
                  label: 'Link Name',
                  required: true,
                  placeholder: 'Enter link name',
                },
              },
              {
                key: 'selfTarget',
                type: 'checkbox',
                defaultValue: false,
                props: {
                  label: 'Open in Same Tab',
                },
              },
            ],
          },
        },
      ],
    },
  ];
};
